import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Images, Inbox, KeyRound, LogOut, Plus, Trash2, Upload, Lock, Unlock, RefreshCw, CheckCheck, Clock, Link as LinkIcon, Pencil, FileText, Save, GripVertical, X, UserCog } from "lucide-react";
import { ICON_OPTIONS, reorder } from "@/data/highlightIcons";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { useNoIndex, SEO_PAGES, DEFAULT_SEO } from "@/lib/seo";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "projects", label: "Projects", icon: Building2 },
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "content", label: "Content", icon: FileText },
  { id: "inquiries", label: "Inquiries", icon: Inbox },
  { id: "portals", label: "Client Portals", icon: KeyRound },
  { id: "account", label: "Account", icon: UserCog },
];

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  useNoIndex("Admin CMS | VG Homes");

  useEffect(() => { if (ready && !user) nav("/admin/login"); }, [ready, user, nav]);
  if (!ready || !user) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-60 bg-navy-void text-white flex flex-col fixed h-full">
        <div className="p-5 border-b border-white/10"><Logo onDark /></div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${tab === t.id ? "bg-teal text-white" : "text-white/70 hover:bg-white/5"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>
        <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout-button"
          className="m-3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/70 hover:bg-white/5"><LogOut className="h-4 w-4" /> Logout</button>
      </aside>

      <div className="flex-1 ml-60">
        <header className="bg-white border-b border-slate-200 px-8 py-4">
          <h1 className="font-display text-xl font-bold text-slate-900 capitalize">{TABS.find((t) => t.id === tab)?.label}</h1>
        </header>
        <main className="p-8">
          {tab === "overview" && <Overview />}
          {tab === "projects" && <ProjectsTab />}
          {tab === "gallery" && <GalleryTab />}
          {tab === "content" && <ContentTab />}
          {tab === "inquiries" && <InquiriesTab />}
          {tab === "portals" && <PortalsTab />}
          {tab === "account" && <AccountTab email={user.email} />}
        </main>
      </div>
    </div>
  );
}

function useUpload() {
  return async (file) => {
    if (file.size > 50 * 1024 * 1024) throw new Error("File is larger than 50 MB");
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };
}

function ImageUploadField({ label, value, onChange, testid, round }) {
  const ref = useRef();
  const upload = useUpload();
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    toast.loading("Uploading...", { id: testid });
    try { const d = await upload(f); onChange(d.url); toast.success("Uploaded — remember to save", { id: testid }); }
    catch (err) { toast.error(apiError(err), { id: testid }); }
  };
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className={`h-14 w-14 shrink-0 overflow-hidden bg-slate-200 ring-1 ring-slate-200 ${round ? "rounded-full" : "rounded-lg"}`}>
        {value ? <img src={value} alt={label} className="h-full w-full object-cover" data-testid={`${testid}-preview`} /> : <div className="flex h-full items-center justify-center text-[10px] text-slate-400">Default</div>}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-600">{label}</p>
        <div className="mt-1 flex gap-2">
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testid}-input`} />
          <button type="button" onClick={() => ref.current?.click()} data-testid={`${testid}-upload`} className="inline-flex items-center gap-1.5 rounded-full border border-navy px-3 py-1 text-xs font-semibold text-navy hover:bg-navy hover:text-white transition-colors"><Upload className="h-3.5 w-3.5" /> {value ? "Replace" : "Upload"}</button>
          {value && <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`} className="rounded-full px-3 py-1 text-xs font-semibold text-red-500 hover:bg-red-50">Remove</button>}
        </div>
      </div>
    </div>
  );
}

function FeatureListEditor({ items, onChange, testid, max = 8 }) {
  const set = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((w, i) => {
          const Opt = ICON_OPTIONS.find((o) => o.value === w.icon);
          return (
            <div key={i} className="space-y-2 rounded-lg bg-slate-50 p-3" data-testid={`${testid}-${i}`}>
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal/10 text-teal">{Opt ? <Opt.Icon className="h-5 w-5" /> : <span className="text-[10px]">Auto</span>}</span>
                <select value={w.icon || ""} onChange={(e) => set(i, "icon", e.target.value)} data-testid={`${testid}-icon-${i}`} className="flex-1 rounded-md border border-slate-200 px-2 py-2 text-sm">
                  <option value="">Default icon</option>
                  {ICON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testid}-delete-${i}`} className="rounded-md p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Input placeholder="Title" value={w.title || ""} onChange={(e) => set(i, "title", e.target.value)} data-testid={`${testid}-title-${i}`} />
              <Input placeholder="Short description" value={w.desc || ""} onChange={(e) => set(i, "desc", e.target.value)} data-testid={`${testid}-desc-${i}`} />
            </div>
          );
        })}
      </div>
      {items.length < max && (
        <button onClick={() => onChange([...items, { title: "", desc: "", icon: "" }])} data-testid={`${testid}-add`} className="inline-flex items-center gap-1.5 rounded-full border border-navy px-4 py-1.5 text-xs font-semibold text-navy hover:bg-navy hover:text-white transition-colors"><Plus className="h-3.5 w-3.5" /> Add item</button>
      )}
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState({ projects: 0, ongoing: 0, inquiries: 0, newInq: 0, media: 0 });
  useEffect(() => {
    Promise.all([api.get("/admin/projects"), api.get("/admin/inquiries"), api.get("/gallery")]).then(([p, i, g]) => {
      setStats({
        projects: p.data.length,
        ongoing: p.data.filter((x) => x.status === "Ongoing").length,
        inquiries: i.data.length,
        newInq: i.data.filter((x) => x.status === "new").length,
        media: g.data.length,
      });
    }).catch(() => {});
  }, []);
  const cards = [
    { label: "Total Projects", value: stats.projects, sub: `${stats.ongoing} ongoing` },
    { label: "Inquiries", value: stats.inquiries, sub: `${stats.newInq} new` },
    { label: "Gallery Media", value: stats.media, sub: "photos & videos" },
  ];
  return (
    <div className="grid gap-6 sm:grid-cols-3">
      {cards.map((c, i) => (
        <div key={i} className="rounded-2xl bg-white p-6 ring-1 ring-slate-100 shadow-sm" data-testid={`stat-card-${i}`}>
          <div className="text-sm text-slate-500">{c.label}</div>
          <div className="mt-2 font-display text-4xl font-extrabold text-navy">{c.value}</div>
          <div className="mt-1 text-xs text-teal-dark font-medium">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_PROJECT = { name: "", location: "", status: "Ongoing", type: "", subtitle: "", units: "", image: "", images: [], highlights: [], completion: "", description: "", progress_percentage: 0, published: true };

function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_PROJECT);
  const [editing, setEditing] = useState(null);
  const [milestoneFor, setMilestoneFor] = useState(null);
  const fileRef = useRef();
  const imagesRef = useRef();
  const upload = useUpload();

  const load = () => api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setForm(EMPTY_PROJECT); setEditing(null); setOpen(true); };
  const openEdit = (p) => {
    const highlights = (p.highlights || []).map((h) => (typeof h === "string" ? { label: h, icon: "building-2" } : h));
    setForm({ ...EMPTY_PROJECT, ...p, highlights });
    setEditing(p.id); setOpen(true);
  };

  const save = async () => {
    if (!form.image) { toast.error("A primary cover image is required"); return; }
    try {
      const payload = { ...form, progress_percentage: Number(form.progress_percentage) || 0 };
      if (editing) await api.put(`/admin/projects/${editing}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("Project saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this project?")) return; await api.delete(`/admin/projects/${id}`); toast.success("Deleted"); load(); };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    toast.loading("Uploading...", { id: "up" });
    try { const d = await upload(f); setForm((p) => ({ ...p, image: d.url })); toast.success("Uploaded", { id: "up" }); }
    catch (err) { toast.error(apiError(err), { id: "up" }); }
  };
  const onImagesFile = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    toast.loading(`Uploading ${files.length} image(s)...`, { id: "imgs" });
    try {
      const urls = [];
      for (const f of files) { const d = await upload(f); urls.push(d.url); }
      setForm((p) => ({ ...p, images: [...(p.images || []), ...urls] }));
      toast.success("Images added", { id: "imgs" });
    } catch (err) { toast.error(apiError(err), { id: "imgs" }); }
  };
  const removeImage = (url) => setForm((p) => ({ ...p, images: (p.images || []).filter((u) => u !== url) }));
  const dragImgIdx = useRef(null);
  const onImgDrop = (i) => {
    const from = dragImgIdx.current;
    if (from === null || from === i) return;
    setForm((p) => ({ ...p, images: reorder(p.images || [], from, i) }));
    dragImgIdx.current = null;
  };

  const addHighlight = () => setForm((p) => ({ ...p, highlights: [...(p.highlights || []), { label: "", icon: "building-2" }] }));
  const setHighlight = (i, key, val) => setForm((p) => { const h = [...(p.highlights || [])]; h[i] = { ...h[i], [key]: val }; return { ...p, highlights: h }; });
  const removeHighlight = (i) => setForm((p) => ({ ...p, highlights: (p.highlights || []).filter((_, k) => k !== i) }));

  return (
    <div>
      <div className="flex justify-end mb-5">
        <button onClick={openNew} data-testid="admin-add-project-button" className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-dark transition-colors"><Plus className="h-4 w-4" /> Add Project</button>
      </div>
      <div className="grid gap-4">
        {projects.map((p) => (
          <div key={p.id} className="flex items-center gap-4 rounded-xl bg-white p-4 ring-1 ring-slate-100" data-testid={`admin-project-row-${p.id}`}>
            <img src={p.image} alt="" className="h-16 w-24 rounded-lg object-cover bg-slate-100" />
            <div className="flex-1">
              <div className="flex items-center gap-2"><span className="font-semibold text-slate-900">{p.name}</span><StatusBadge status={p.status} /></div>
              <div className="text-sm text-slate-500">{p.location}</div>
            </div>
            <button onClick={() => setMilestoneFor(p)} data-testid={`admin-milestones-${p.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" title="Timeline"><Clock className="h-4 w-4" /></button>
            <button onClick={() => openEdit(p)} data-testid={`admin-edit-project-${p.id}`} className="rounded-lg p-2 text-navy hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => del(p.id)} data-testid={`admin-delete-project-${p.id}`} className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-project-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Project name" value={form.name} onChange={set("name")} data-testid="project-form-name" />
            <Input placeholder="Location" value={form.location} onChange={set("location")} data-testid="project-form-location" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.status} onChange={set("status")} data-testid="project-form-status" className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                <option>Ongoing</option><option>Completed</option>
              </select>
              <Input type="number" placeholder="Progress %" value={form.progress_percentage} onChange={set("progress_percentage")} data-testid="project-form-progress" />
            </div>
            <Input placeholder="Type (e.g. Residential Apartment)" value={form.type} onChange={set("type")} data-testid="project-form-type" />
            <Input placeholder="Subtitle (e.g. Contemporary Urban Living)" value={form.subtitle} onChange={set("subtitle")} data-testid="project-form-subtitle" />
            <Input placeholder="Configuration / Units" value={form.units} onChange={set("units")} />
            <Input placeholder="Completion (e.g. Q4 2026)" value={form.completion} onChange={set("completion")} />
            <Textarea placeholder="Description" value={form.description} onChange={set("description")} data-testid="project-form-description" />

            <div data-testid="project-form-highlights">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-500">Highlights & icons (shown on detail page)</label>
                <button onClick={addHighlight} data-testid="highlight-add" className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"><Plus className="h-3.5 w-3.5" /> Add</button>
              </div>
              <div className="mt-2 space-y-2">
                {(form.highlights || []).map((h, i) => {
                  const Opt = ICON_OPTIONS.find((o) => o.value === h.icon) || ICON_OPTIONS[0];
                  return (
                    <div key={i} className="flex items-center gap-2" data-testid={`highlight-row-${i}`}>
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-teal/10 text-teal"><Opt.Icon className="h-4 w-4" /></span>
                      <select value={h.icon} onChange={(e) => setHighlight(i, "icon", e.target.value)} data-testid={`highlight-icon-${i}`} className="w-36 shrink-0 rounded-md border border-slate-200 px-2 py-2 text-sm">
                        {ICON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <Input placeholder="Highlight label" value={h.label} onChange={(e) => setHighlight(i, "label", e.target.value)} data-testid={`highlight-label-${i}`} />
                      <button onClick={() => removeHighlight(i)} data-testid={`highlight-remove-${i}`} className="shrink-0 rounded-md p-2 text-red-500 hover:bg-red-50"><X className="h-4 w-4" /></button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500">Primary cover image <span className="text-red-500">*</span></label>
              <div className="mt-1 flex items-center gap-2">
                <Input placeholder="Cover image URL" value={form.image} onChange={set("image")} data-testid="project-form-image" />
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
                <button onClick={() => fileRef.current?.click()} className="shrink-0 rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50" data-testid="project-form-upload"><Upload className="h-4 w-4" /></button>
              </div>
              {form.image && <img src={form.image} alt="" className="mt-2 h-32 w-full rounded-lg object-cover" />}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-500">Additional images (gallery)</label>
                <input ref={imagesRef} type="file" accept="image/*" multiple className="hidden" onChange={onImagesFile} />
                <button onClick={() => imagesRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50" data-testid="project-form-images-upload"><Upload className="h-3.5 w-3.5" /> Add Images</button>
              </div>
              {(form.images || []).length > 0 && (
                <div className="mt-2 grid grid-cols-4 gap-2" data-testid="project-form-images-grid">
                  {(form.images || []).map((url, i) => (
                    <div key={url} draggable onDragStart={() => { dragImgIdx.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onImgDrop(i)}
                      className="group relative overflow-hidden rounded-lg aspect-square bg-slate-100 cursor-move ring-1 ring-slate-200" data-testid={`project-image-${i}`}>
                      <img src={url} alt="" className="h-full w-full object-cover pointer-events-none" />
                      <span className="absolute left-1 top-1 rounded bg-black/50 p-0.5 text-white"><GripVertical className="h-3 w-3" /></span>
                      <button onClick={() => removeImage(url)} className="absolute top-1 right-1 rounded-full bg-red-500 p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              {(form.images || []).length > 1 && <p className="mt-1 text-[11px] text-slate-400">Drag images to reorder how they appear in the gallery.</p>}
            </div>

            <button onClick={save} data-testid="project-form-save" className="w-full rounded-full bg-navy py-2.5 font-semibold text-white hover:bg-navy-dark transition-colors">Save Project</button>
          </div>
        </DialogContent>
      </Dialog>

      {milestoneFor && <MilestoneManager project={milestoneFor} onClose={() => setMilestoneFor(null)} />}
    </div>
  );
}

const EMPTY_MILESTONE = { date: "", title: "", description: "", status: "Completed", media: [] };

function MilestoneManager({ project, onClose }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_MILESTONE);
  const fileRef = useRef();
  const upload = useUpload();

  const load = () => api.get(`/admin/milestones?project_id=${project.id}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [project.id]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const add = async () => {
    if (!form.date || !form.title) { toast.error("Date and title required"); return; }
    try { await api.post("/admin/milestones", { ...form, project_id: project.id }); toast.success("Milestone added"); setForm(EMPTY_MILESTONE); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/milestones/${id}`); load(); };
  const dragIdx = useRef(null);
  const onDrop = async (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const newItems = reorder(items, from, i);
    setItems(newItems);
    dragIdx.current = null;
    try { await api.post("/admin/milestones/reorder", { project_id: project.id, ids: newItems.map((m) => m.id) }); toast.success("Order saved"); }
    catch (e) { toast.error(apiError(e)); load(); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    toast.loading("Uploading...", { id: "m" });
    try { const d = await upload(f); setForm((p) => ({ ...p, media: [...p.media, { type: d.type, url: d.url }] })); toast.success("Added", { id: "m" }); }
    catch (err) { toast.error(apiError(err), { id: "m" }); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-milestone-dialog">
        <DialogHeader><DialogTitle>Timeline · {project.name}</DialogTitle></DialogHeader>
        <div className="rounded-xl bg-slate-50 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input type="date" value={form.date} onChange={set("date")} data-testid="milestone-form-date" />
            <select value={form.status} onChange={set("status")} data-testid="milestone-form-status" className="rounded-md border border-slate-200 px-3 py-2 text-sm">
              <option>Completed</option><option>In Progress</option><option>Upcoming</option>
            </select>
          </div>
          <Input placeholder="Milestone title" value={form.title} onChange={set("title")} data-testid="milestone-form-title" />
          <Textarea placeholder="Description" value={form.description} onChange={set("description")} data-testid="milestone-form-desc" />
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={onFile} />
            <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-white" data-testid="milestone-upload"><Upload className="h-4 w-4" /> Add photo/video</button>
            <span className="text-xs text-slate-400">{form.media.length} attached</span>
          </div>
          {form.media.length > 0 && <div className="flex gap-2 flex-wrap">{form.media.map((m, i) => <span key={i} className="rounded bg-teal/10 px-2 py-1 text-xs text-teal-dark">{m.type}</span>)}</div>}
          <button onClick={add} data-testid="milestone-form-save" className="w-full rounded-full bg-navy py-2.5 text-sm font-semibold text-white hover:bg-navy-dark transition-colors">Add Milestone</button>
        </div>
        <div className="space-y-2 mt-2">
          {items.length > 1 && <p className="text-[11px] text-slate-400 px-1">Drag milestones to set the order shown to clients.</p>}
          {items.map((m, i) => (
            <div key={m.id} draggable onDragStart={() => { dragIdx.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
              className="flex items-center gap-3 rounded-lg bg-white p-3 ring-1 ring-slate-100 cursor-move" data-testid={`milestone-row-${m.id}`}>
              <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
              <span className="text-xs text-slate-400 w-24">{m.date}</span>
              <span className="flex-1 text-sm font-medium text-slate-800">{m.title}</span>
              <span className="text-xs text-teal-dark">{m.status}</span>
              <button onClick={() => del(m.id)} className="text-red-500 hover:bg-red-50 rounded p-1"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GalleryTab() {
  const [media, setMedia] = useState([]);
  const [projects, setProjects] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [caption, setCaption] = useState("");
  const [progress, setProgress] = useState(null);
  const fileRef = useRef();
  const upload = useUpload();

  const load = () => api.get("/gallery").then((r) => setMedia(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);

  const folder = projects.find((p) => p.id === folderId);
  const items = media.filter((m) => m.project_id === folderId);
  const unassigned = media.filter((m) => !projects.some((p) => p.id === m.project_id));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length || !folder) return;
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setProgress(`Uploading ${i + 1} of ${files.length}...`);
      try {
        const d = await upload(files[i]);
        await api.post("/admin/gallery", { project_id: folder.id, project_name: folder.name, type: d.type, url: d.url, caption });
        ok++;
      } catch (err) { toast.error(`${files[i].name}: ${apiError(err)}`); }
    }
    setProgress(null); setCaption("");
    if (ok) toast.success(`${ok} file(s) added to ${folder.name}`);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this media?")) return;
    try { await api.delete(`/admin/gallery/${id}`); toast.success("Deleted"); } catch (e) { toast.error(apiError(e)); }
    load();
  };

  if (!folder) {
    return (
      <div>
        <p className="mb-5 text-sm text-slate-500">Open a project folder to upload or manage its photos and videos.</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-gallery-folders">
          {projects.map((p) => {
            const list = media.filter((m) => m.project_id === p.id);
            const cover = list.find((m) => m.type !== "video")?.url || p.image;
            return (
              <button key={p.id} onClick={() => setFolderId(p.id)} data-testid={`admin-gallery-folder-${p.id}`}
                className="group overflow-hidden rounded-xl bg-white text-left ring-1 ring-slate-100 hover:shadow-lg transition-shadow">
                <div className="relative aspect-[4/3] bg-slate-100">
                  {cover && <img src={cover} alt="" className="h-full w-full object-cover" />}
                  <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg bg-white/90 text-navy"><Images className="h-4 w-4" /></span>
                </div>
                <div className="p-3">
                  <p className="font-semibold text-slate-900 truncate">{p.name}</p>
                  <p className="text-xs text-slate-500" data-testid={`admin-gallery-folder-count-${p.id}`}>{list.length} item(s)</p>
                </div>
              </button>
            );
          })}
        </div>
        {unassigned.length > 0 && <p className="mt-6 text-xs text-amber-600">{unassigned.length} older item(s) are not linked to any project.</p>}
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => setFolderId(null)} data-testid="admin-gallery-back" className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-teal">← All folders</button>
      <div className="rounded-xl bg-white p-4 ring-1 ring-slate-100 mb-6 flex flex-wrap items-center gap-3" data-testid="admin-gallery-uploader">
        <div className="mr-auto">
          <p className="font-semibold text-slate-900" data-testid="admin-gallery-folder-name">{folder.name}</p>
          <p className="text-xs text-slate-500">{items.length} item(s) · photos & videos up to 50 MB</p>
        </div>
        <Input placeholder="Caption (optional, applies to this upload)" value={caption} onChange={(e) => setCaption(e.target.value)} className="max-w-xs" data-testid="gallery-caption-input" />
        <input ref={fileRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={onFiles} data-testid="gallery-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={!!progress} data-testid="gallery-upload-button"
          className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-dark disabled:opacity-60 transition-colors"><Upload className="h-4 w-4" /> {progress || "Upload Media"}</button>
      </div>
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-gallery-grid">
        {items.map((m) => (
          <div key={m.id} className="group relative overflow-hidden rounded-xl aspect-[4/3] bg-slate-100" data-testid={`admin-gallery-item-${m.id}`}>
            {m.type === "video" ? <video src={m.url} className="h-full w-full object-cover" preload="metadata" /> : <img src={m.url} alt="" className="h-full w-full object-cover" />}
            {m.type === "video" && <span className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">VIDEO</span>}
            <button onClick={() => del(m.id)} data-testid={`admin-gallery-delete-${m.id}`} className="absolute top-2 right-2 rounded-full bg-red-500 p-1.5 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
            {m.caption && <div className="absolute bottom-0 inset-x-0 bg-black/60 p-2 text-xs text-white truncate">{m.caption}</div>}
          </div>
        ))}
      </div>
      {items.length === 0 && <p className="py-12 text-center text-slate-400" data-testid="admin-gallery-empty">This folder is empty. Upload photos or videos above.</p>}
    </div>
  );
}

function ContentTab() {
  const [home, setHome] = useState(null);
  const [services, setServices] = useState(null);
  const [testis, setTestis] = useState(null);
  const [allProjects, setAllProjects] = useState([]);

  useEffect(() => {
    api.get("/admin/projects").then((r) => setAllProjects(r.data)).catch(() => {});
    api.get("/content/home").then((r) => setHome(r.data || {})).catch(() => setHome({}));
    api.get("/content/testimonials").then((r) => setTestis(r.data?.items || [])).catch(() => setTestis([]));
    api.get("/content/services").then((r) => setServices(r.data || { services: [] })).catch(() => setServices({ services: [] }));
  }, []);

  const saveHome = async () => { try { await api.put("/admin/content/home", { data: { ...home, intro_points: (home.intro_points || []).map((s) => s.trim()).filter(Boolean) } }); toast.success("Home content saved"); } catch (e) { toast.error(apiError(e)); } };
  const saveServices = async () => {
    const data = { ...services, services: (services.services || []).map((s) => ({ ...s, points: (s.points || []).map((p) => p.trim()).filter(Boolean) })) };
    try { await api.put("/admin/content/services", { data }); toast.success("Services page saved"); } catch (e) { toast.error(apiError(e)); }
  };
  const setStat = (i, k, v) => { const s = [...(home.stats || [])]; s[i] = { ...s[i], [k]: v }; setHome({ ...home, stats: s }); };
  const setT = (i, k, v) => { const s = [...testis]; s[i] = { ...s[i], [k]: v }; setTestis(s); };
  const saveTestis = async () => { try { await api.put("/admin/content/testimonials", { data: { items: testis } }); toast.success("Testimonials saved"); } catch (e) { toast.error(apiError(e)); } };
  const field = (k, ph, Comp = Input) => <Comp placeholder={ph} value={home[k] || ""} onChange={(e) => setHome({ ...home, [k]: e.target.value })} data-testid={`content-${k}`} />;

  if (!home || !services || !testis) return <p className="text-slate-400">Loading...</p>;

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-100" data-testid="content-home-editor">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Home Page</h3>
        <div className="space-y-3">
          <Input placeholder="Hero title" value={home.hero_title || ""} onChange={(e) => setHome({ ...home, hero_title: e.target.value })} data-testid="content-hero-title" />
          <Textarea placeholder="Hero subtitle" value={home.hero_subtitle || ""} onChange={(e) => setHome({ ...home, hero_subtitle: e.target.value })} data-testid="content-hero-subtitle" />
          <ImageUploadField label="Hero banner photo (wide, 1920px+ recommended)" value={home.hero_image} onChange={(u) => setHome((h) => ({ ...h, hero_image: u }))} testid="content-hero-image" />
          <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Featured Projects (home page, up to 3)</p>
          <div className="grid gap-2 sm:grid-cols-3" data-testid="content-featured-picker">
            {[0, 1, 2].map((i) => {
              const ids = home.featured_ids || [];
              return (
                <select key={i} value={ids[i] || ""} data-testid={`content-featured-${i}`}
                  onChange={(e) => { const next = [...ids]; next[i] = e.target.value; setHome({ ...home, featured_ids: next }); }}
                  className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                  <option value="">{`Slot ${i + 1}: auto (latest)`}</option>
                  {allProjects.filter((p) => p.id === ids[i] || !ids.includes(p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              );
            })}
          </div>
          <Input placeholder="Intro title" value={home.intro_title || ""} onChange={(e) => setHome({ ...home, intro_title: e.target.value })} />
          <Textarea placeholder="Intro body" value={home.intro_body || ""} onChange={(e) => setHome({ ...home, intro_body: e.target.value })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <ImageUploadField label="Who We Are main photo" value={home.intro_image} onChange={(u) => setHome((h) => ({ ...h, intro_image: u }))} testid="content-intro-image" />
            <ImageUploadField label="&quot;Modern Living&quot; card photo" value={home.intro_small_image} onChange={(u) => setHome((h) => ({ ...h, intro_small_image: u }))} testid="content-intro-small-image" />
          </div>
          <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Who We Are — key points (one per line)</p>
          <Textarea rows={3} value={(home.intro_points || []).join("\n")} onChange={(e) => setHome({ ...home, intro_points: e.target.value.split("\n") })} data-testid="content-intro-points" />
          <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Who We Are — feature cards</p>
          <FeatureListEditor items={home.pillars || []} onChange={(v) => setHome((h) => ({ ...h, pillars: v }))} testid="content-pillars" max={4} />
          <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Stats bar</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(home.stats || []).map((s, i) => (
              <div key={i} className="space-y-2">
                <Input placeholder="Value" value={s.value || ""} onChange={(e) => setStat(i, "value", e.target.value)} />
                <Input placeholder="Label" value={s.label || ""} onChange={(e) => setStat(i, "label", e.target.value)} />
              </div>
            ))}
          </div>
          <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Why Choose section</p>
          {field("why_title", "Section title")}
          {field("why_body", "Section text", Textarea)}
          <ImageUploadField label="Why Choose image" value={home.why_image} onChange={(u) => setHome((h) => ({ ...h, why_image: u }))} testid="content-why-image" />
          <FeatureListEditor items={home.why_choose || []} onChange={(v) => setHome((h) => ({ ...h, why_choose: v }))} testid="content-why" max={6} />
          <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Homebuyers & NRI cards</p>
          {field("homebuyers_desc", "For Homebuyers text", Textarea)}
          {field("nri_desc", "For NRI Investors text", Textarea)}
          <div className="grid gap-3 sm:grid-cols-2">
            <ImageUploadField label="Homebuyers image" value={home.homebuyers_image} onChange={(u) => setHome((h) => ({ ...h, homebuyers_image: u }))} testid="content-homebuyers-image" />
            <ImageUploadField label="NRI Investors image" value={home.nri_image} onChange={(u) => setHome((h) => ({ ...h, nri_image: u }))} testid="content-nri-image" />
          </div>
          <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Bottom call-to-action</p>
          {field("cta_title", "CTA title")}
          {field("cta_body", "CTA text")}
          <button onClick={saveHome} data-testid="content-save-home" className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-dark transition-colors"><Save className="h-4 w-4" /> Save Home</button>
        </div>
      </div>

      <ServicesEditor page={services} setPage={setServices} onSave={saveServices} />
      <SeoEditor />

      <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-100" data-testid="content-testimonials-editor">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Client Testimonials</h3>
        <div className="space-y-4">
          {testis.map((t, i) => (
            <div key={i} className="space-y-2 rounded-lg bg-slate-50 p-3" data-testid={`content-testimonial-${i}`}>
              <ImageUploadField round label="Client photo" value={t.photo} onChange={(u) => setTestis((arr) => arr.map((x, k) => (k === i ? { ...x, photo: u } : x)))} testid={`content-testimonial-photo-${i}`} />
              <Textarea placeholder="Quote" value={t.quote || ""} onChange={(e) => setT(i, "quote", e.target.value)} data-testid={`content-testimonial-quote-${i}`} />
              <div className="grid gap-2 sm:grid-cols-[1fr_1fr_90px_auto]">
                <Input placeholder="Name" value={t.name || ""} onChange={(e) => setT(i, "name", e.target.value)} />
                <Input placeholder="Role / location" value={t.role || ""} onChange={(e) => setT(i, "role", e.target.value)} />
                <Input type="number" min={1} max={5} placeholder="Stars" value={t.rating || 5} onChange={(e) => setT(i, "rating", Number(e.target.value))} />
                <button onClick={() => setTestis(testis.filter((_, k) => k !== i))} data-testid={`content-testimonial-delete-${i}`} className="rounded-md p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-3">
            <button onClick={() => setTestis([...testis, { quote: "", name: "", role: "", rating: 5 }])} data-testid="content-testimonial-add" className="inline-flex items-center gap-2 rounded-full border border-navy px-5 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors"><Plus className="h-4 w-4" /> Add Testimonial</button>
            <button onClick={saveTestis} data-testid="content-save-testimonials" className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-dark transition-colors"><Save className="h-4 w-4" /> Save Testimonials</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const EMPTY_SERVICE = { title: "", tagline: "", desc: "", body: "", icon: "building-2", image: "", points: [] };

function ServiceItemEditor({ s, i, total, update, move, remove }) {
  const set = (k) => (e) => { const v = e.target.value; update((x) => ({ ...x, [k]: v })); };
  return (
    <div className="rounded-xl bg-slate-50 p-4 space-y-2 ring-1 ring-slate-100" data-testid={`svc-item-${i}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Service {i + 1}</span>
        <div className="flex gap-1">
          <button onClick={() => move(-1)} disabled={i === 0} data-testid={`svc-up-${i}`} className="rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-white disabled:opacity-30">↑</button>
          <button onClick={() => move(1)} disabled={i === total - 1} data-testid={`svc-down-${i}`} className="rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-white disabled:opacity-30">↓</button>
          <button onClick={remove} data-testid={`svc-delete-${i}`} className="rounded-md p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <ImageUploadField label="Primary image *" value={s.image} onChange={(u) => update((x) => ({ ...x, image: u }))} testid={`svc-image-${i}`} />
      <div className="grid gap-2 sm:grid-cols-[1fr_180px]">
        <Input placeholder="Service title" value={s.title || ""} onChange={set("title")} data-testid={`svc-title-${i}`} />
        <select value={s.icon || ""} onChange={set("icon")} data-testid={`svc-icon-${i}`} className="rounded-md border border-slate-200 px-2 py-2 text-sm">
          {ICON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <Input placeholder="Tagline (small text above title)" value={s.tagline || ""} onChange={set("tagline")} data-testid={`svc-tagline-${i}`} />
      <Textarea rows={2} placeholder="Short summary" value={s.desc || ""} onChange={set("desc")} data-testid={`svc-desc-${i}`} />
      <Textarea rows={4} placeholder="Detailed content" value={s.body || ""} onChange={set("body")} data-testid={`svc-body-${i}`} />
      <Textarea rows={4} placeholder="Key points / inclusions (one per line)" value={(s.points || []).join("\n")} onChange={(e) => { const v = e.target.value.split("\n"); update((x) => ({ ...x, points: v })); }} data-testid={`svc-points-${i}`} />
    </div>
  );
}

function ServicesEditor({ page, setPage, onSave }) {
  const list = page.services || [];
  const setList = (fn) => setPage((p) => ({ ...p, services: fn(p.services || []) }));
  const f = (k, ph, Comp = Input) => <Comp placeholder={ph} value={page[k] || ""} onChange={(e) => setPage((p) => ({ ...p, [k]: e.target.value }))} data-testid={`svc-page-${k}`} />;
  const save = () => {
    if (list.some((s) => !s.title || !s.image)) { toast.error("Every service needs a title and a primary image"); return; }
    onSave();
  };
  return (
    <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-100 space-y-3" data-testid="content-services-editor">
      <h3 className="font-display text-lg font-semibold text-slate-900">Services Page</h3>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Page banner</p>
      {f("hero_title", "Page title")}
      {f("hero_subtitle", "Page subtitle", Textarea)}
      <ImageUploadField label="Banner background photo" value={page.hero_image} onChange={(u) => setPage((p) => ({ ...p, hero_image: u }))} testid="svc-hero-image" />
      <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Services</p>
      {list.map((s, i) => (
        <ServiceItemEditor key={i} s={s} i={i} total={list.length}
          update={(fn) => setList((l) => l.map((x, j) => (j === i ? fn(x) : x)))}
          move={(d) => setList((l) => reorder(l, i, i + d))}
          remove={() => { if (window.confirm("Remove this service?")) setList((l) => l.filter((_, j) => j !== i)); }} />
      ))}
      <button onClick={() => setList((l) => [...l, { ...EMPTY_SERVICE }])} data-testid="svc-add" className="inline-flex items-center gap-1.5 rounded-full border border-navy px-4 py-2 text-xs font-semibold text-navy hover:bg-navy hover:text-white transition-colors"><Plus className="h-3.5 w-3.5" /> Add Service</button>
      <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">How We Work steps</p>
      {f("process_title", "Section title")}
      <FeatureListEditor items={page.process || []} onChange={(v) => setPage((p) => ({ ...p, process: v }))} testid="svc-process" max={6} />
      <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Bottom call-to-action</p>
      {f("cta_title", "CTA title")}
      {f("cta_body", "CTA text")}
      <button onClick={save} data-testid="content-save-services" className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-dark transition-colors"><Save className="h-4 w-4" /> Save Services Page</button>
    </div>
  );
}

function SeoField({ label, value, onChange, max, testid, area }) {
  const Comp = area ? Textarea : Input;
  const len = (value || "").length;
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-500 mb-1"><span>{label}</span>{max && <span className={len > max ? "text-red-500 font-semibold" : ""} data-testid={`${testid}-count`}>{len}/{max}</span>}</div>
      <Comp value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={area ? 2 : undefined} />
    </div>
  );
}

function SeoEditor() {
  const [seo, setSeo] = useState(null);
  useEffect(() => {
    api.get("/content/seo").then((r) => {
      const d = r.data || {};
      const pages = Object.fromEntries(SEO_PAGES.map(({ key }) => [key, { ...DEFAULT_SEO.pages[key], ...Object.fromEntries(Object.entries(d.pages?.[key] || {}).filter(([, v]) => v)) }]));
      setSeo({ share_image: d.share_image || "", pages });
    }).catch(() => setSeo({ share_image: "", pages: DEFAULT_SEO.pages }));
  }, []);
  if (!seo) return null;
  const setPage = (key, k, v) => setSeo((s) => ({ ...s, pages: { ...s.pages, [key]: { ...s.pages[key], [k]: v } } }));
  const save = async () => { try { await api.put("/admin/content/seo", { data: seo }); toast.success("SEO settings saved"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-100 space-y-4" data-testid="content-seo-editor">
      <div>
        <h3 className="font-display text-lg font-semibold text-slate-900">SEO & Meta Tags</h3>
        <p className="text-xs text-slate-500">Ideal: title up to 60–70 characters, description up to 160. Keywords separated by commas.</p>
      </div>
      <ImageUploadField label="Social share image (WhatsApp / Facebook preview)" value={seo.share_image} onChange={(u) => setSeo((s) => ({ ...s, share_image: u }))} testid="seo-share-image" />
      {SEO_PAGES.map(({ key, label }) => (
        <div key={key} className="rounded-xl bg-slate-50 p-4 space-y-2 ring-1 ring-slate-100" data-testid={`seo-page-${key}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label} page</p>
          <SeoField label="Meta title" max={70} value={seo.pages[key]?.title} onChange={(v) => setPage(key, "title", v)} testid={`seo-${key}-title`} />
          <SeoField label="Meta description" max={160} area value={seo.pages[key]?.description} onChange={(v) => setPage(key, "description", v)} testid={`seo-${key}-description`} />
          <SeoField label="Keywords" area value={seo.pages[key]?.keywords} onChange={(v) => setPage(key, "keywords", v)} testid={`seo-${key}-keywords`} />
        </div>
      ))}
      <button onClick={save} data-testid="content-save-seo" className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-dark transition-colors"><Save className="h-4 w-4" /> Save SEO Settings</button>
    </div>
  );
}

function InquiriesTab() {  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/inquiries").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (id) => { await api.put(`/admin/inquiries/${id}`); load(); };
  const del = async (id) => { await api.delete(`/admin/inquiries/${id}`); load(); };

  return (
    <div className="space-y-4">
      {items.length === 0 && <p className="text-slate-400">No inquiries yet.</p>}
      {items.map((q) => (
        <div key={q.id} className={`rounded-xl bg-white p-5 ring-1 ${q.status === "new" ? "ring-teal/40" : "ring-slate-100"}`} data-testid={`admin-inquiry-${q.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900">{q.name}</span>
                {q.status === "new" && <span className="rounded-full bg-teal/15 px-2 py-0.5 text-xs font-semibold text-teal-dark">New</span>}
              </div>
              <div className="text-sm text-slate-500">{q.email} · {q.phone || "no phone"}</div>
              {q.project_interest && <div className="text-xs text-slate-400 mt-0.5">Interest: {q.project_interest}</div>}
              <p className="mt-2 text-sm text-slate-700">{q.message}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => toggle(q.id)} data-testid={`inquiry-toggle-${q.id}`} className="rounded-lg p-2 text-teal hover:bg-teal/10" title="Toggle handled"><CheckCheck className="h-4 w-4" /></button>
              <button onClick={() => del(q.id)} className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_PW = { current: "", next: "", confirm: "" };

function AccountTab({ email }) {
  const [f, setF] = useState(EMPTY_PW);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const strong = f.next.length >= 8 && /\d/.test(f.next) && /[a-z]/i.test(f.next);
  const submit = async (e) => {
    e.preventDefault();
    if (!strong) { toast.error("Use at least 8 characters with letters and numbers"); return; }
    if (f.next !== f.confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: f.current, new_password: f.next }); toast.success("Password updated"); setF(EMPTY_PW); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="max-w-md rounded-2xl bg-white p-6 ring-1 ring-slate-100 space-y-3" data-testid="account-password-form">
      <h3 className="font-display text-lg font-semibold text-slate-900">Change Password</h3>
      <p className="text-sm text-slate-500">Signed in as <span className="font-medium text-slate-700" data-testid="account-email">{email}</span></p>
      <Input type="password" placeholder="Current password" value={f.current} onChange={set("current")} autoComplete="current-password" data-testid="account-current-password" />
      <Input type="password" placeholder="New password" value={f.next} onChange={set("next")} autoComplete="new-password" data-testid="account-new-password" />
      <Input type="password" placeholder="Confirm new password" value={f.confirm} onChange={set("confirm")} autoComplete="new-password" data-testid="account-confirm-password" />
      <p className={`text-xs ${f.next && !strong ? "text-red-500" : "text-slate-400"}`}>At least 8 characters, including letters and numbers.</p>
      <button type="submit" disabled={busy || !f.current || !f.next} data-testid="account-password-submit" className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-dark disabled:opacity-50 transition-colors"><Save className="h-4 w-4" /> {busy ? "Updating..." : "Update Password"}</button>
    </form>
  );
}

function PortalsTab() {
  const [portals, setPortals] = useState([]);
  const [pinInputs, setPinInputs] = useState({});
  const load = () => api.get("/admin/portals").then((r) => setPortals(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const origin = window.location.origin;
  const setPin = (id, v) => setPinInputs({ ...pinInputs, [id]: v });

  const generate = async (id) => {
    const pin = pinInputs[id];
    if (!/^\d{4}$/.test(pin || "")) { toast.error("Enter a 4-digit PIN"); return; }
    try { await api.post(`/admin/portals/${id}/generate`, { pin }); toast.success("Portal ready"); setPin(id, ""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const rotate = async (id) => {
    const pin = pinInputs[id];
    if (!/^\d{4}$/.test(pin || "")) { toast.error("Enter a 4-digit PIN"); return; }
    try { await api.post(`/admin/portals/${id}/rotate-pin`, { pin }); toast.success("PIN rotated"); setPin(id, ""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const unlock = async (id) => { try { await api.post(`/admin/portals/${id}/unlock`); toast.success("Portal unlocked"); load(); } catch (e) { toast.error(apiError(e)); } };
  const copyLink = (token) => { navigator.clipboard.writeText(`${origin}/portal/${token}`); toast.success("Link copied"); };

  return (
    <div className="space-y-4">
      {portals.map((p) => (
        <div key={p.project_id} className="rounded-xl bg-white p-5 ring-1 ring-slate-100" data-testid={`admin-portal-${p.project_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900">{p.project_name}</span>
                {p.locked ? <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-600"><Lock className="h-3 w-3" /> Locked</span>
                  : p.has_portal ? <span className="inline-flex items-center gap-1 rounded-full bg-teal/15 px-2 py-0.5 text-xs font-semibold text-teal-dark"><Unlock className="h-3 w-3" /> Active</span>
                  : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">Not set up</span>}
              </div>
              {p.has_portal && <div className="mt-1 text-xs text-slate-400">Failed attempts: {p.failed_attempts}/3</div>}
            </div>
            {p.has_portal && (
              <button onClick={() => copyLink(p.token)} data-testid={`portal-copy-link-${p.project_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-4 py-2 text-xs font-semibold text-navy hover:bg-navy hover:text-white transition-colors"><LinkIcon className="h-3.5 w-3.5" /> Copy Link</button>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Input placeholder="4-digit PIN" maxLength={4} value={pinInputs[p.project_id] || ""} onChange={(e) => setPin(p.project_id, e.target.value.replace(/\D/g, ""))} className="max-w-[140px]" data-testid={`portal-pin-input-${p.project_id}`} />
            {p.has_portal ? (
              <>
                <button onClick={() => rotate(p.project_id)} data-testid={`portal-rotate-${p.project_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white hover:bg-navy-dark transition-colors"><RefreshCw className="h-3.5 w-3.5" /> Rotate PIN</button>
                {p.locked && <button onClick={() => unlock(p.project_id)} data-testid={`portal-unlock-${p.project_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-teal px-4 py-2 text-xs font-semibold text-white hover:bg-teal-dark transition-colors"><Unlock className="h-3.5 w-3.5" /> Unlock</button>}
              </>
            ) : (
              <button onClick={() => generate(p.project_id)} data-testid={`portal-generate-${p.project_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white hover:bg-navy-dark transition-colors"><KeyRound className="h-3.5 w-3.5" /> Generate Access</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
